import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { WarningOctagon, X, ArrowSquareOut } from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE as API } from "../lib/apiBase";
import { getLayoutBootstrap } from "../lib/layoutBootstrap"; // B1099

/**
 * Bannière budget Emergent LLM — B800 puis B881, FUSIONNÉES en B1109
 * (Eric 2026-09-08).
 *
 * Deux bannières coexistaient et se contredisaient : une barre rouge collée
 * en haut (B800, sonde 60 s, masquable 24 h, visible seulement quand le
 * budget était déjà BLOQUÉ) et un encadré dans la page (B881, sonde 5 min,
 * avec coût, budget restant et boutons Recharger/Réinitialiser). Deux sondes
 * pour la même information, dont une chaque minute sur toutes les pages.
 *
 * Choix d'Eric : UNE barre collée en haut (la plus visible) AVEC les
 * informations et les boutons de l'encadré. Elle s'affiche dès que le budget
 * est FAIBLE — attendre le blocage, c'est découvrir la panne quand le
 * pipeline a déjà échoué en silence.
 */
const POLL_MS = 5 * 60 * 1000;
const DISMISS_KEY = "knoria_llm_budget_dismissed_at";
const DISMISS_MS = 24 * 3600_000;

export default function LlmBudgetBanner() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [resetting, setResetting] = useState(false);

  const isAdmin = user?.role === "admin" || user?.is_founder || user?.role === "platform_admin";

  useEffect(() => {
    // Masquage 24 h repris de B800 : sans lui, Eric revoyait la barre à chaque
    // page alors qu'il avait déjà pris la décision de recharger plus tard.
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (ts && Date.now() - parseInt(ts, 10) < DISMISS_MS) setHidden(true);
    } catch { /* localStorage indisponible */ }
  }, []);

  const fetchStatus = useCallback(async ({ fromBootstrap = false } = {}) => {
    if (!isAdmin) return;
    try {
      // B1099 — Premier appel servi par l'appel agrégé du gabarit (cache
      // 20 s) ; le poll 5 min et « Réinitialiser » gardent l'endpoint dédié.
      if (fromBootstrap) {
        const boot = await getLayoutBootstrap();
        if (boot?.llm_budget) {
          setStatus(boot.llm_budget);
          return;
        }
      }
      const r = await axios.get(`${API}/api/admin/llm-budget-status`, { withCredentials: true });
      setStatus(r.data || null);
    } catch {
      // 403 (non-admin) ou 5xx → on se taît, pas de toast en boucle.
      setStatus(null);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStatus({ fromBootstrap: true });
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [isAdmin, fetchStatus]);

  const resetBudget = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/api/admin/llm-budget-status/reset`, {}, { withCredentials: true });
      await fetchStatus();
    } catch { /* silencieux */ } finally {
      setResetting(false);
    }
  };

  if (!isAdmin || hidden || !status?.low_budget) return null;

  const pct = status.pct_remaining;
  const cost = status.current_cost;
  const max = status.max_budget;
  const lastEv = status.last_event;
  const isExceeded = status.blocked && (pct == null || pct <= 0);

  return (
    <div
      role="alert"
      className={`sticky top-0 z-[65] flex items-start gap-2 px-4 py-2 text-xs text-white ${
        isExceeded ? "bg-red-700" : "bg-amber-600"
      }`}
      data-testid="llm-budget-banner"
    >
      <WarningOctagon size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold" data-testid="llm-budget-banner-title">
          {isExceeded
            ? (lang === "en"
                ? "Emergent LLM budget exhausted — the pipeline is failing silently."
                : "Budget Emergent LLM épuisé — le pipeline échoue silencieusement.")
            : (lang === "en"
                ? `Emergent LLM budget low (${pct != null ? pct.toFixed(1) : "?"}% remaining).`
                : `Budget Emergent LLM faible (${pct != null ? pct.toFixed(1) : "?"} % restant).`)}
        </span>{" "}
        <span className="opacity-90" data-testid="llm-budget-banner-detail">
          {cost != null && max != null && (
            <span data-testid="llm-budget-banner-cost">
              {lang === "en"
                ? `Cost: $${cost.toFixed(2)} / $${max.toFixed(2)}. `
                : `Coût : ${cost.toFixed(2)} $ / ${max.toFixed(2)} $. `}
            </span>
          )}
          {status.recent_count > 0 && (
            <span data-testid="llm-budget-banner-recent-count">
              {lang === "en"
                ? `${status.recent_count} error(s) in the last hour. `
                : `${status.recent_count} erreur(s) dans la dernière heure. `}
            </span>
          )}
          {lang === "en"
            ? "Top up via Profile → Manage plan → Universal Key → Add Balance."
            : "Rechargez via Profil → Manage plan → Universal Key → Add Balance."}
          {lastEv?.source && (
            <span className="opacity-80" data-testid="llm-budget-banner-last-source">
              {lang === "en" ? " Last source: " : " Dernière source : "}
              <code className="font-mono">{lastEv.source}</code>
            </span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href="https://app.emergent.sh/profile/manage-plan"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-semibold px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 border border-white/30"
          data-testid="llm-budget-banner-topup-btn"
        >
          {lang === "en" ? "Top up" : "Recharger"}
          <ArrowSquareOut size={12} />
        </a>
        <button
          type="button"
          onClick={resetBudget}
          disabled={resetting}
          className="inline-flex items-center font-medium px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 border border-white/30 disabled:opacity-50"
          data-testid="llm-budget-banner-reset-btn"
          title={lang === "en"
            ? "Purge the error buffer after topping up"
            : "Purge le buffer d'erreurs après un rechargement"}
        >
          {resetting
            ? (lang === "en" ? "Resetting…" : "Purge…")
            : (lang === "en" ? "Reset" : "Réinitialiser")}
        </button>
        <button
          type="button"
          onClick={() => {
            try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
            setHidden(true);
          }}
          className="p-1 rounded hover:bg-white/20"
          data-testid="llm-budget-banner-dismiss-btn"
          aria-label={lang === "en" ? "Hide for 24 hours" : "Masquer pendant 24 heures"}
          title={lang === "en" ? "Hide for 24 hours" : "Masquer pendant 24 heures"}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
